import { motion } from "framer-motion";
import {
    Package, Wand2, ArrowRight, Wrench, PenTool, Ruler, DollarSign, Euro, TrendingUp, Tag, SlidersHorizontal,
} from "lucide-react";
import { SectionHead } from "@/components/inquiry/inquiryFields";
import { PACKAGE_STEPS, CUSTOM_STEPS } from "@/components/inquiry/inquiryState";

/**
 * StepRoute — the entry step: choose between the Package Journey and the
 * Custom Project Inquiry. Both are silver glass cards with a clockwise white
 * edge-light; selecting one plays a glare sweep. Hovering / selecting reveals
 * a little cluster of "process" tool icons, and a mini animated journey preview
 * runs a light down the 01 -> N steps. The Custom card carries its own colour.
 */
export const StepRoute = ({ data, onSelect }) => {
    const options = [
        {
            id: "package",
            title: "Package Inquiry",
            summary: "Pick a curated package — logo system or brand identity ecosystem — with transparent, upfront pricing.",
            icon: Package,
            steps: PACKAGE_STEPS,
            cta: "Explore packages",
            highlight: "Fixed pricing",
            highlightIcon: Tag,
            proc: [Wrench, PenTool],
        },
        {
            id: "custom",
            title: "Custom Project Inquiry",
            summary: "Have something unique in mind? Pick your services and share the vision — we'll craft a bespoke Custom Engagement proposal.",
            icon: Wand2,
            steps: CUSTOM_STEPS,
            cta: "Design a custom project",
            highlight: "Custom scope",
            highlightIcon: SlidersHorizontal,
            proc: [Ruler, DollarSign, Euro, TrendingUp],
            variant: "custom",
        },
    ];

    return (
        <section data-testid="step-route">
            <SectionHead
                eyebrow="Step 01 · Route"
                title="How would you like to start?"
                description="Pick the path that fits your project. You can always change direction — nothing is submitted until the final step."
            />

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2" data-testid="route-cards">
                {options.map((opt, idx) => {
                    const Icon = opt.icon;
                    const HIcon = opt.highlightIcon;
                    const isSelected = data.inquiry_kind === opt.id;
                    return (
                        <motion.button
                            key={opt.id}
                            type="button"
                            onClick={() => onSelect(opt.id)}
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.08 * idx, ease: [0.16, 1, 0.3, 1] }}
                            whileHover={{ y: -4 }}
                            data-testid={`route-option-${opt.id}`}
                            data-selected={isSelected ? "true" : "false"}
                            className={`pg-route-card ${opt.variant === "custom" ? "pg-route-card--custom" : ""} group flex h-full flex-col items-start rounded-3xl p-6 text-left sm:p-8`}
                        >
                            <span className="pg-route-card__ring" aria-hidden="true" />
                            <span className="pg-route-card__shine" aria-hidden="true" />
                            <span className="pg-route-card__glare" aria-hidden="true" />

                            <div className="relative z-[1] flex w-full items-start justify-between">
                                <div className="pg-route-card__lead">
                                    <span className="pg-route-card__icon">
                                        <Icon className="h-5 w-5" strokeWidth={1.7} />
                                    </span>
                                    <span className="pg-route-card__proc" aria-hidden="true">
                                        {opt.proc.map((PI, i) => (
                                            <span key={i} className="pg-route-card__proc-i" style={{ transitionDelay: `${i * 70}ms` }}>
                                                <PI className="h-3.5 w-3.5" strokeWidth={1.7} />
                                            </span>
                                        ))}
                                    </span>
                                </div>
                                {opt.highlight && (
                                    <span className="pg-route-card__badge">
                                        <HIcon className="h-3 w-3" strokeWidth={2} />
                                        {opt.highlight}
                                    </span>
                                )}
                            </div>

                            <h3 className="pg-route-card__title font-display mt-6 text-2xl sm:text-3xl">
                                {opt.title}
                            </h3>
                            <p className="relative z-[1] mt-3 text-sm leading-relaxed" style={{ color: "var(--pg-fg-muted)" }}>
                                {opt.summary}
                            </p>

                            {/* Mini animated journey preview (a light runs 01 -> N) */}
                            <div className="pg-route-steps" data-testid={`route-steps-${opt.id}`}>
                                <span className="pg-route-steps__line" aria-hidden="true">
                                    <span className="pg-route-steps__fill" />
                                </span>
                                {opt.steps.map((s, i) => (
                                    <span key={s.id} className="pg-route-steps__item" style={{ "--i": i }}>
                                        <span className="pg-route-steps__no">{i + 1}</span>
                                        <span className="pg-route-steps__label">{s.label}</span>
                                    </span>
                                ))}
                            </div>

                            <div className="pg-route-card__cta">
                                {opt.cta}
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.8} />
                            </div>
                        </motion.button>
                    );
                })}
            </div>

            <p className="mt-8 text-xs" style={{ color: "var(--pg-fg-muted)" }}>
                Not sure yet? Start with a package — you can adjust scope on the next steps.
            </p>
        </section>
    );
};

export default StepRoute;
